import { Button, Col, DatePicker, Input, Row, Skeleton } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import React, { FC, useEffect, useRef, useState } from "react";
import type { DatePickerProps } from "antd";
import dayjs from "dayjs";
import { IPatientFilter } from "../../interfaces/IPatient";
import { LIST_DEFAULT_PAGE_SIZE } from "../../utils/constants";

type FiltersProps = {
  filters: any;
  handleFiltersChange: CallableFunction;
  searchPlaceholder: string;
};

const Filters: FC<FiltersProps> = ({
  filters,
  handleFiltersChange,
  searchPlaceholder,
}) => {
  const dateFormat = "DD-MM-YYYY";

  const [localStartDate, setLocalStartDate] = useState("");
  const [localEndDate, setLocalEndDate] = useState("");
  const [searchString, setSearchString] = useState("");

  // End date change handler
  const handleEndDateChange: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {
    setLocalEndDate(dateString as string);
    // handleFiltersChange({ ...filters, endDate: dateString });
  };

  // Start date change handler
  const handleStartDateChange: DatePickerProps["onChange"] = (
    date,
    dateString
  ) => {
    setLocalStartDate(dateString as string);
    // handleFiltersChange({ ...filters, startDate: dateString });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearchClick();
    }
  };

  useEffect(() => {
    setSearchString(filters?.searchTerm);
  }, [filters]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (searchString?.length < 4) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set the new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      onSearchClick();
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchString]);

  const onSearchClick = () => {
    handleFiltersChange({
      ...filters,
      endDate: localEndDate,
      startDate: localStartDate,
      searchTerm: searchString,
      pageNumber: 0,
    });
  };

  // On search name change
  const onSearchNameChange = (e: any) => {
    setSearchString(e.target.value);
    if (e.target.value === "") {
      handleFiltersChange({
        ...filters,
        endDate: localEndDate,
        startDate: localStartDate,
        searchTerm: "",
        pageNumber: 0,
      });
    }
  };

  // Set start and end date when filters are changed
  useEffect(() => {
    setLocalStartDate(filters.startDate);
    setLocalEndDate(filters.endDate);
  }, [filters]);

  return (
    <Row style={{ marginBottom: "2em" }} gutter={[16, 16]}>
      <Col>
        <Input
          onChange={onSearchNameChange}
          value={searchString}
          placeholder={searchPlaceholder}
          onKeyDown={onKeyDown}
        />
      </Col>
      {localStartDate !== "" ? (
        <Col>
          FROM DATE :{" "}
          <DatePicker
            onChange={handleStartDateChange}
            value={localStartDate ? dayjs(localStartDate, dateFormat) : null}
            format={dateFormat}
            allowClear={false}
            disabledDate={(current) => {
              return current && current > dayjs().endOf("day");
            }}
            placeholder="SELECT DATE"
          />
        </Col>
      ) : (
        <Skeleton.Input active />
      )}
      {localEndDate !== "" ? (
        <Col>
          TO DATE :{" "}
          <DatePicker
            onChange={handleEndDateChange}
            value={localEndDate ? dayjs(localEndDate, dateFormat) : null}
            format={dateFormat}
            allowClear={false}
            disabledDate={(current) => {
              return current && current > dayjs().endOf("day");
            }}
            placeholder="SELECT DATE"
          />
        </Col>
      ) : (
        <Skeleton.Input style={{ marginLeft: "1em" }} active />
      )}
      <Col>
        {/* TODO: Add a condition for disabling this button */}
        <Button
          onClick={onSearchClick}
          type="primary"
          icon={<SearchOutlined />}
          iconPosition="start"
        >
          SEARCH
        </Button>
      </Col>
    </Row>
  );
};

export default Filters;
